import { LogOut, Menu, Moon, Sun, UserRound, X } from "lucide-react";
import { useTheme } from "next-themes";
import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { adminNavigationGroups } from "../../app/admin-navigation";
import { WorkspaceOutlet } from "../../app/workspace-outlet";
import { defaultAdminPath, visibleAdminSurfaces } from "../../auth/access-policy";
import { useAuth } from "../../auth/auth-context";
import { BrandLogo } from "../brand-logo";
import { useDestinationContext } from "../destination-context";
import { PageHeaderActivityProvider } from "../shared-ui";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

/** Cyra shell: persistent navigation, one content area, and an accessible mobile menu. */
export function AdminDashboardBlock() {
  const auth = useAuth(),
    location = useLocation(),
    navigate = useNavigate();
  const destination = useDestinationContext();
  const { resolvedTheme, setTheme } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigationRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const wasMenuOpen = useRef(false);
  useEffect(() => {
    if (!menuOpen) {
      if (wasMenuOpen.current) menuButtonRef.current?.focus();
      wasMenuOpen.current = false;
      return;
    }
    wasMenuOpen.current = true;
    const navigation = navigationRef.current;
    navigation?.querySelector<HTMLButtonElement>("button")?.focus();
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        menuButtonRef.current?.focus();
      }
      if (event.key !== "Tab") return;
      const stops = Array.from(
        navigation?.querySelectorAll<HTMLElement>("a[href], button:not([disabled])") ?? [],
      ).filter((element) => element.getClientRects().length);
      const first = stops[0],
        last = stops.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [menuOpen]);
  const surfaces = visibleAdminSurfaces(auth.actor);
  const name = `${auth.actor?.firstName ?? ""} ${auth.actor?.lastName ?? ""}`.trim();
  return (
    <div className="cyra-shell" data-menu-open={menuOpen}>
      <a className="cyra-skip" href="#main-content">
        Skip to main content
      </a>
      <aside ref={navigationRef} className="cyra-navigation" aria-label="Workspace">
        <div className="cyra-brand">
          <NavLink to={defaultAdminPath(auth.actor)}>
            <BrandLogo inverse />
            <span className="cyra-admin-label">ADMIN</span>
          </NavLink>
          <Button
            variant="ghost"
            size="icon"
            className="cyra-mobile-control"
            aria-label="Close navigation"
            onClick={() => {
              setMenuOpen(false);
              menuButtonRef.current?.focus();
            }}
          >
            <X aria-hidden />
          </Button>
        </div>
        <nav aria-label="Admin navigation">
          {adminNavigationGroups.map((group) => (
            <section key={group.name}>
              <h2>{group.name}</h2>
              <ul>
                {group.paths
                  .flatMap((path) => surfaces.filter((surface) => surface.path === path))
                  .map(({ path, label, icon: Icon }) => (
                    <li key={path}>
                      <NavLink
                        to={destination?.href(path) ?? path}
                        onClick={() => setMenuOpen(false)}
                      >
                        <Icon aria-hidden size={17} />
                        {label}
                      </NavLink>
                    </li>
                  ))}
              </ul>
            </section>
          ))}
        </nav>
        <div className="cyra-account-menu">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="w-full justify-start">
                <UserRound aria-hidden />
                <span className="truncate">{name || "My account"}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" align="start">
              <DropdownMenuItem
                onSelect={() => {
                  setMenuOpen(false);
                  void navigate(`/admins/${auth.actor?.userId}`);
                }}
              >
                My profile
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
              >
                {resolvedTheme === "dark" ? (
                  <Sun aria-hidden size={16} />
                ) : (
                  <Moon aria-hidden size={16} />
                )}
                Switch theme
              </DropdownMenuItem>
              {auth.workspace.failures.length ? (
                <DropdownMenuItem onSelect={() => void auth.retryWorkspace()}>
                  Retry unavailable pages
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => {
                  void auth.logout().then(() => navigate("/login", { replace: true }));
                }}
              >
                <LogOut aria-hidden size={16} />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>
      <main inert={menuOpen} id="main-content" tabIndex={-1} className="cyra-main">
        <div className="cyra-workspace-bar">
          <Button
            variant="ghost"
            size="icon"
            className="cyra-mobile-control"
            ref={menuButtonRef}
            aria-label="Open navigation"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(true)}
          >
            <Menu aria-hidden />
          </Button>
        </div>
        <PageHeaderActivityProvider active>
          <WorkspaceOutlet activePath={location.pathname} />
        </PageHeaderActivityProvider>
      </main>
    </div>
  );
}
